import * as React from 'react';
import { ReactNode } from 'react';

import { cn } from '@/utils/tailwind';

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="w-full overflow-x-auto overflow-y-hidden">
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('text-primary-foreground bg-primary font-medium', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'data-[state=selected]:bg-muted group border-b border-secondary transition-colors [thead_&]:bg-secondary',
      className
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & {
    spacingVariant?: 'wide' | 'tight';
  }
>(({ className, spacingVariant, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-12 whitespace-nowrap bg-tertiary px-2 text-left align-middle font-semibold text-tertiary [&:has([role=checkbox])]:pr-0',
      spacingVariant === 'wide' && 'px-5',
      className
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    spacingVariant?: 'wide' | 'tight';
  }
>(({ className, spacingVariant, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'text-ellipsis p-2 py-4 align-middle [&:has([role=checkbox])]:pr-0',
      spacingVariant === 'wide' && 'p-5',
      className
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm font-bold text-gray-500', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export function TableCellTextOverflow(props: {
  children?: ReactNode;
  className?: string;
  spacingVariant?: 'wide' | 'tight';
}) {
  return (
    <div
      className={cn(
        'absolute inset-0 flex items-center p-2 py-4',
        props.spacingVariant === 'wide' && 'p-5',
        props.className
      )}
    >
      <div className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
        {props.children}
      </div>
    </div>
  );
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
